import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  AccordionActions,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Snackbar,
  Alert,
  Tooltip,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  OndemandVideo as VideoIcon,
  Article as ArticleIcon,
  Assignment as AssignmentIcon,
} from '@mui/icons-material';

import {
  createModule,
  updateModule,
  deleteModule,
  createContent,
  updateContent,
  deleteContent,
  getCourseModulesAdmin,
  getCourseContentsAdmin,
} from '../services/api.js';

const AdminCourseDetailsPage = () => {
  const { id } = useParams(); // courseId
  const navigate = useNavigate();

  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Expanded accordion state
  const [expanded, setExpanded] = useState(false);

  // Dialog states for Module
  const [openModuleDialog, setOpenModuleDialog] = useState(false);
  const [editingModule, setEditingModule] = useState(null);
  const [moduleForm, setModuleForm] = useState({
    nome: '',
    descricao: '',
    ordem: 1,
  });

  // Dialog states for Content
  const [openContentDialog, setOpenContentDialog] = useState(false);
  const [editingContent, setEditingContent] = useState(null);
  const [selectedModuleId, setSelectedModuleId] = useState(null);
  const [contentForm, setContentForm] = useState({
    titulo: '',
    descricao: '',
    id_tipo: 1, // Default to video or text
    ordem: 1,
    url_vimeo: '',
    id_vimeo: '',
    duracao_minutos: 0,
    peso_avaliacao: 0,
    qtd_tentativas: 1,
  });

  const fetchModules = useCallback(async () => {
    try {
      setLoading(true);
      // Here, the backend requires cursoVersaoId instead of just cursoId.
      // Assuming for now that courseId == cursoVersaoId or we need an endpoint to get active version.
      // We will try using the 'id' from URL as 'cursoVersaoId'.
      const modulesData = await getCourseModulesAdmin(id);

      const modulesList =
        modulesData.modulos ||
        modulesData.items ||
        (Array.isArray(modulesData) ? modulesData : []);

      // Fetch contents for each module to display the full structure
      const modulesWithContents = await Promise.all(
        modulesList.map(async mod => {
          try {
            const contentsData = await getCourseContentsAdmin(
              mod.id_curso_modulo
            );
            return {
              ...mod,
              conteudos:
                contentsData.conteudos ||
                contentsData.items ||
                (Array.isArray(contentsData) ? contentsData : []),
            };
          } catch (err) {
            console.error(
              'Error fetching contents for module',
              mod.id_curso_modulo,
              err
            );
            return { ...mod, conteudos: [] };
          }
        })
      );

      setModules(modulesWithContents.sort((a, b) => a.ordem - b.ordem));
    } catch (err) {
      setError('Erro ao carregar módulos: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchModules();
  }, [fetchModules]);

  const handleChangeAccordion = panel => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  // --- Module Handlers ---

  const handleOpenModuleDialog = (module = null) => {
    if (module) {
      setEditingModule(module);
      setModuleForm({
        nome: module.nome || '',
        descricao: module.descricao || '',
        ordem: module.ordem || 1,
      });
    } else {
      setEditingModule(null);
      setModuleForm({
        nome: '',
        descricao: '',
        ordem: modules.length + 1,
      });
    }
    setOpenModuleDialog(true);
  };

  const handleCloseModuleDialog = () => {
    setOpenModuleDialog(false);
    setEditingModule(null);
  };

  const submitModule = async () => {
    try {
      setLoading(true);
      const submitData = new window.FormData();
      submitData.append('nome', moduleForm.nome);
      submitData.append('descricao', moduleForm.descricao);
      submitData.append('ordem', moduleForm.ordem);
      submitData.append('id_curso_versao', id); // associate with course

      if (editingModule) {
        // API patch uses JSON
        await updateModule(editingModule.id_curso_modulo, {
          nome: moduleForm.nome,
          descricao: moduleForm.descricao,
          ordem: moduleForm.ordem,
        });
        setSuccess('Módulo atualizado!');
      } else {
        await createModule(submitData);
        setSuccess('Módulo criado!');
      }
      handleCloseModuleDialog();
      fetchModules();
    } catch (err) {
      setError('Erro ao salvar módulo: ' + err.message);
      setLoading(false);
    }
  };

  const handleDeleteModule = async moduleId => {
    if (
      window.confirm(
        'Tem certeza que deseja excluir este módulo e todo o seu conteúdo?'
      )
    ) {
      try {
        setLoading(true);
        await deleteModule(moduleId);
        setSuccess('Módulo excluído!');
        fetchModules();
      } catch (err) {
        setError('Erro ao excluir módulo: ' + err.message);
        setLoading(false);
      }
    }
  };

  // --- Content Handlers ---

  const handleOpenContentDialog = (moduleId, content = null) => {
    setSelectedModuleId(moduleId);
    if (content) {
      setEditingContent(content);
      setContentForm({
        titulo: content.titulo || '',
        descricao: content.descricao || '',
        id_tipo: content.id_tipo || 1,
        ordem: content.ordem || 1,
        url_vimeo: content.url_vimeo || '',
        id_vimeo: content.id_vimeo || '',
        duracao_minutos: content.duracao_minutos || 0,
        peso_avaliacao: content.peso_avaliacao || 0,
        qtd_tentativas: content.qtd_tentativas || 1,
      });
    } else {
      const moduleObj = modules.find(m => m.id_curso_modulo === moduleId);
      setEditingContent(null);
      setContentForm({
        titulo: '',
        descricao: '',
        id_tipo: 1, // Text by default
        ordem:
          moduleObj && moduleObj.conteudos ? moduleObj.conteudos.length + 1 : 1,
        url_vimeo: '',
        id_vimeo: '',
        duracao_minutos: 0,
        peso_avaliacao: 0,
        qtd_tentativas: 1,
      });
    }
    setOpenContentDialog(true);
  };

  const handleCloseContentDialog = () => {
    setOpenContentDialog(false);
    setEditingContent(null);
    setSelectedModuleId(null);
  };

  const submitContent = async () => {
    try {
      setLoading(true);
      const submitData = new window.FormData();
      submitData.append('id_curso_modulo', selectedModuleId);
      submitData.append('titulo', contentForm.titulo);
      submitData.append('descricao', contentForm.descricao);
      submitData.append('id_tipo', contentForm.id_tipo);
      submitData.append('ordem', contentForm.ordem);

      if (contentForm.url_vimeo)
        submitData.append('url_vimeo', contentForm.url_vimeo);
      if (contentForm.id_vimeo)
        submitData.append('id_vimeo', contentForm.id_vimeo);
      submitData.append('duracao_minutos', contentForm.duracao_minutos);
      submitData.append('peso_avaliacao', contentForm.peso_avaliacao);
      submitData.append('qtd_tentativas', contentForm.qtd_tentativas);

      if (editingContent) {
        await updateContent(editingContent.id_curso_conteudo, {
          titulo: contentForm.titulo,
          descricao: contentForm.descricao,
          id_tipo: contentForm.id_tipo,
          ordem: contentForm.ordem,
          url_vimeo: contentForm.url_vimeo,
          id_vimeo: contentForm.id_vimeo,
          duracao_minutos: contentForm.duracao_minutos,
          peso_avaliacao: contentForm.peso_avaliacao,
          qtd_tentativas: contentForm.qtd_tentativas,
        });
        setSuccess('Conteúdo atualizado!');
      } else {
        await createContent(submitData);
        setSuccess('Conteúdo criado!');
      }
      handleCloseContentDialog();
      fetchModules();
    } catch (err) {
      setError('Erro ao salvar conteúdo: ' + err.message);
      setLoading(false);
    }
  };

  const handleDeleteContent = async contentId => {
    if (window.confirm('Tem certeza que deseja excluir este conteúdo?')) {
      try {
        setLoading(true);
        await deleteContent(contentId);
        setSuccess('Conteúdo excluído!');
        fetchModules();
      } catch (err) {
        setError('Erro ao excluir conteúdo: ' + err.message);
        setLoading(false);
      }
    }
  };

  const getContentIcon = tipoId => {
    switch (tipoId) {
      case 2: // Vídeo
        return <VideoIcon color='primary' />;
      case 3: // Avaliação
        return <AssignmentIcon color='secondary' />;
      default: // Texto/Apostila
        return <ArticleIcon color='action' />;
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4, gap: 2 }}>
        <IconButton onClick={() => navigate('/dashboard')}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant='h5' component='h1'>
          Gerenciar Estrutura do Curso (ID: {id})
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          variant='contained'
          startIcon={<AddIcon />}
          onClick={() => handleOpenModuleDialog()}
        >
          Novo Módulo
        </Button>
      </Box>

      {loading && !modules.length ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : modules.length === 0 ? (
        <Typography
          variant='body1'
          color='textSecondary'
          align='center'
          sx={{ mt: 4 }}
        >
          Nenhum módulo cadastrado para este curso.
        </Typography>
      ) : (
        modules.map(module => (
          <Accordion
            key={module.id_curso_modulo}
            expanded={expanded === `panel${module.id_curso_modulo}`}
            onChange={handleChangeAccordion(`panel${module.id_curso_modulo}`)}
            sx={{ mb: 2 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography
                sx={{ width: '33%', flexShrink: 0, fontWeight: 'bold' }}
              >
                Módulo {module.ordem}: {module.nome}
              </Typography>
              <Typography sx={{ color: 'text.secondary' }}>
                {module.conteudos ? module.conteudos.length : 0} conteúdos
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ width: '100%' }}>
                <Typography variant='body2' paragraph>
                  {module.descricao}
                </Typography>

                {module.conteudos && module.conteudos.length > 0 ? (
                  <List>
                    {module.conteudos
                      .sort((a, b) => a.ordem - b.ordem)
                      .map(content => (
                        <ListItem key={content.id_curso_conteudo} divider>
                          <Box sx={{ mr: 2 }}>
                            {getContentIcon(content.id_tipo)}
                          </Box>
                          <ListItemText
                            primary={`${content.ordem}. ${content.titulo}`}
                            secondary={content.descricao || 'Sem descrição'}
                          />
                          <ListItemSecondaryAction>
                            <Tooltip title='Editar Conteúdo'>
                              <IconButton
                                edge='end'
                                onClick={() =>
                                  handleOpenContentDialog(
                                    module.id_curso_modulo,
                                    content
                                  )
                                }
                                sx={{ mr: 1 }}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title='Excluir Conteúdo'>
                              <IconButton
                                edge='end'
                                color='error'
                                onClick={() =>
                                  handleDeleteContent(content.id_curso_conteudo)
                                }
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                  </List>
                ) : (
                  <Typography
                    variant='body2'
                    color='text.secondary'
                    sx={{ fontStyle: 'italic', my: 2 }}
                  >
                    Nenhum conteúdo neste módulo.
                  </Typography>
                )}

                <Box
                  sx={{ mt: 2, display: 'flex', justifyContent: 'flex-start' }}
                >
                  <Button
                    size='small'
                    startIcon={<AddIcon />}
                    onClick={() =>
                      handleOpenContentDialog(module.id_curso_modulo)
                    }
                  >
                    Adicionar Conteúdo
                  </Button>
                </Box>
              </Box>
            </AccordionDetails>
            <AccordionActions>
              <Button
                size='small'
                color='info'
                onClick={() => handleOpenModuleDialog(module)}
              >
                Editar Módulo
              </Button>
              <Button
                size='small'
                color='error'
                onClick={() => handleDeleteModule(module.id_curso_modulo)}
              >
                Excluir Módulo
              </Button>
            </AccordionActions>
          </Accordion>
        ))
      )}

      {/* DIALOG: MÓDULO */}
      <Dialog
        open={openModuleDialog}
        onClose={handleCloseModuleDialog}
        fullWidth
        maxWidth='sm'
      >
        <DialogTitle>
          {editingModule ? 'Editar Módulo' : 'Novo Módulo'}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label='Nome do Módulo'
              value={moduleForm.nome}
              onChange={e =>
                setModuleForm({ ...moduleForm, nome: e.target.value })
              }
              fullWidth
              required
            />
            <TextField
              label='Descrição'
              value={moduleForm.descricao}
              onChange={e =>
                setModuleForm({ ...moduleForm, descricao: e.target.value })
              }
              fullWidth
              multiline
              rows={3}
            />
            <TextField
              label='Ordem'
              type='number'
              value={moduleForm.ordem}
              onChange={e =>
                setModuleForm({ ...moduleForm, ordem: e.target.value })
              }
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModuleDialog} disabled={loading}>
            Cancelar
          </Button>
          <Button
            onClick={submitModule}
            variant='contained'
            disabled={loading || !moduleForm.nome}
          >
            {loading ? <CircularProgress size={24} /> : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* DIALOG: CONTEÚDO */}
      <Dialog
        open={openContentDialog}
        onClose={handleCloseContentDialog}
        fullWidth
        maxWidth='sm'
      >
        <DialogTitle>
          {editingContent ? 'Editar Conteúdo' : 'Novo Conteúdo'}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label='Título'
              value={contentForm.titulo}
              onChange={e =>
                setContentForm({ ...contentForm, titulo: e.target.value })
              }
              fullWidth
              required
            />
            <TextField
              label='Descrição'
              value={contentForm.descricao}
              onChange={e =>
                setContentForm({ ...contentForm, descricao: e.target.value })
              }
              fullWidth
              multiline
              rows={2}
            />
            <TextField
              label='Tipo (1=Texto/Vídeo, 2=Vídeo Link, 3=Avaliação)'
              type='number'
              value={contentForm.id_tipo}
              onChange={e =>
                setContentForm({ ...contentForm, id_tipo: e.target.value })
              }
              fullWidth
            />
            <TextField
              label='Ordem'
              type='number'
              value={contentForm.ordem}
              onChange={e =>
                setContentForm({ ...contentForm, ordem: e.target.value })
              }
              fullWidth
            />
            <TextField
              label='Duração Estimada (minutos)'
              type='number'
              value={contentForm.duracao_minutos}
              onChange={e =>
                setContentForm({
                  ...contentForm,
                  duracao_minutos: e.target.value,
                })
              }
              fullWidth
            />

            {Number(contentForm.id_tipo) === 2 && (
              <>
                <TextField
                  label='Vimeo Video ID'
                  value={contentForm.id_vimeo}
                  onChange={e =>
                    setContentForm({ ...contentForm, id_vimeo: e.target.value })
                  }
                  fullWidth
                />
              </>
            )}

            {Number(contentForm.id_tipo) === 3 && (
              <>
                <TextField
                  label='Peso Avaliação'
                  type='number'
                  value={contentForm.peso_avaliacao}
                  onChange={e =>
                    setContentForm({
                      ...contentForm,
                      peso_avaliacao: e.target.value,
                    })
                  }
                  fullWidth
                />
                <TextField
                  label='Qtd Tentativas'
                  type='number'
                  value={contentForm.qtd_tentativas}
                  onChange={e =>
                    setContentForm({
                      ...contentForm,
                      qtd_tentativas: e.target.value,
                    })
                  }
                  fullWidth
                />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseContentDialog} disabled={loading}>
            Cancelar
          </Button>
          <Button
            onClick={submitContent}
            variant='contained'
            disabled={loading || !contentForm.titulo}
          >
            {loading ? <CircularProgress size={24} /> : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* SNACKBARS */}
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
      >
        <Alert
          onClose={() => setSuccess(null)}
          severity='success'
          sx={{ width: '100%' }}
        >
          {success}
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert
          onClose={() => setError(null)}
          severity='error'
          sx={{ width: '100%' }}
        >
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminCourseDetailsPage;
